import { useCallback, useEffect, useRef, useState } from "react";
import { authFetch } from "../auth-context";
import type { DelegateArchive, DelegateTimelineEntry } from "./types";
import { mergeTimelineEntries, parseTimelinePage, timelinePath, validArchive, type TimelineScope } from "./delegateTimeline";
import { recordAuthenticatedPublicSegments } from "./publicProgressProof";

type State = { key:string; entries:DelegateTimelineEntry[]; olderCursor:string|null;
  afterCursor:string|null; loading:boolean; error:boolean };
export function useDelegateTimeline(scope: TimelineScope, incoming: DelegateTimelineEntry[], archive?: DelegateArchive) {
  const key = (scope.provider ?? "astra") + ":" + scope.companyId + ":" + scope.runId + ":" + scope.requestMessageId;
  const marker = validArchive(archive);
  const enabled = !!marker && marker.runId === scope.runId && marker.requestMessageId === scope.requestMessageId;
  const [state,setState] = useState<State>({key,entries:incoming,olderCursor:null,afterCursor:null,loading:false,error:false});
  const latest = useRef(state); latest.current = state;
  const mountedKey = useRef(key); mountedKey.current = key;
  const controller = useRef<AbortController|null>(null);
  const incomingKey = JSON.stringify(incoming);
  useEffect(() => {
    setState(previous => previous.key !== key
      ? {key,entries:incoming,olderCursor:null,afterCursor:null,loading:false,error:false}
      : {...previous,entries:mergeTimelineEntries(previous.entries,incoming)});
  // Incoming is a bounded status snapshot; signature avoids a render-effect loop.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key,incomingKey]);
  const fetchPage = useCallback(async (older = false) => {
    if (!enabled) return;
    controller.current?.abort();
    const request = new AbortController(); controller.current = request;
    const current = latest.current.key === key ? latest.current : undefined;
    let cursor = older ? current?.olderCursor : current?.afterCursor;
    setState(previous => ({...previous,loading:true,error:false}));
    try {
      do {
        const params = new URLSearchParams({requestMessageId:scope.requestMessageId,limit:"50"});
        if (cursor) params.set(older ? "before" : "after",cursor);
        const response = await authFetch(timelinePath(scope) + "?" + params, {signal:request.signal});
        if (!response.ok) throw new Error("Archive unavailable");
        const page = parseTimelinePage(await response.json(),scope);
        if (request.signal.aborted || mountedKey.current !== key) return;
        recordAuthenticatedPublicSegments(scope,page.entries);
        setState(previous => ({key, entries:mergeTimelineEntries(previous.key === key ? previous.entries : incoming,page.entries),
          olderCursor:older || !current?.afterCursor ? page.olderCursor : previous.olderCursor,
          afterCursor:older ? previous.afterCursor : page.afterCursor,
          loading:!older && page.hasMoreAfter,error:false}));
        if (older || !page.hasMoreAfter || !page.afterCursor || page.afterCursor === cursor) break;
        cursor = page.afterCursor;
      } while (!request.signal.aborted);
      if (!request.signal.aborted && mountedKey.current === key) setState(previous => ({...previous,loading:false}));
    } catch {
      if (!request.signal.aborted && mountedKey.current === key) setState(previous => ({...previous,loading:false,error:true}));
    }
  // Scope strings determine identity; incoming is the initial fallback only.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key,enabled]);
  useEffect(() => {
    void fetchPage();
    return () => controller.current?.abort();
  }, [fetchPage,enabled ? marker?.lastSequence : undefined]);
  const displayed = state.key === key ? state.entries : incoming;
  return {entries:mergeTimelineEntries(displayed,incoming),hasOlder:state.key === key && !!state.olderCursor,
    loading:state.key === key && state.loading,error:state.key === key && state.error,
    loadOlder:() => fetchPage(true),retry:() => fetchPage()};
}
